/**
 * Prev / numbered / Next paginator shown below the grid.
 *
 * ─── Why these are ANCHORS (CRAWL-2/3) ──────────────────────────────────────
 * This used to render bare `<Button onClick>` controls while `page` lived only
 * in `useProductsQuery`'s `useState`. Net effect: `/products?page=2` did not
 * exist as a URL. Nothing linked to page 2, nothing could be crawled past the
 * first 24 products, and a middle-click or "open in new tab" did nothing.
 *
 * Now it is progressive enhancement: every control is a real
 * `<a href="/products?...&page=N">` (crawlers, middle-click, ⌘-click, copy-link
 * all work), and a plain left-click is intercepted to do the fast in-page
 * refetch instead of a full navigation. The href is built from the CURRENT
 * search params, so an active filter set is preserved in the paginated URL.
 *
 * Pairs with `productsCanonicalUrl` in `lib/products-page-seed.ts` — without a
 * page-aware canonical, every one of these URLs would have been canonicalised
 * back to `/products` and the fix would have been worthless.
 */

"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";

interface ProductPaginationProps {
  page: number;
  total: number;
  limit: number;
  onChange: (nextPage: number) => void;
  /** Path the page links point at. Defaults to `/products`. */
  basePath?: string;
}

/** Windowed page numbers: always first + last, plus a ±1 window around `page`. */
export function pageWindow(page: number, totalPages: number): (number | "gap")[] {
  const wanted = new Set<number>([1, totalPages, page - 1, page, page + 1]);
  const pages = [...wanted]
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);
  const out: (number | "gap")[] = [];
  let prev = 0;
  for (const p of pages) {
    if (prev && p - prev > 1) out.push("gap");
    out.push(p);
    prev = p;
  }
  return out;
}

export function ProductPagination({
  page,
  total,
  limit,
  onChange,
  basePath = "/products",
}: ProductPaginationProps) {
  const searchParams = useSearchParams();
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;

  /** Current query with `page` set (omitted for page 1 — it IS the canonical). */
  function hrefFor(nextPage: number): string {
    const params = new URLSearchParams(searchParams?.toString() ?? "");
    // The quick-view deep-link param would re-open a drawer on the new page.
    params.delete("product");
    if (nextPage <= 1) params.delete("page");
    else params.set("page", String(nextPage));
    const qs = params.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }

  // Return the viewport to the top of the grid on page change, otherwise the
  // re-rendered grid loads above the current scroll offset and the change is
  // invisible (reads as "nothing happened"), especially on mobile. No-op if
  // the grid anchor isn't on the page.
  function goTo(nextPage: number) {
    onChange(nextPage);
    if (typeof document !== "undefined") {
      document.getElementById("product-grid")?.scrollIntoView({ block: "start" });
    }
  }

  /** Plain left-click → fast in-page refetch. Modifier/middle click → follow
   *  the href (new tab / new window), exactly like the product cards do. */
  function handleClick(e: React.MouseEvent, nextPage: number) {
    if (
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      (e.nativeEvent as MouseEvent & { button?: number }).button === 1
    ) {
      return;
    }
    e.preventDefault();
    goTo(nextPage);
  }

  const linkClass =
    "inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-9";
  const activeClass =
    "inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-primary bg-primary px-3 text-sm font-semibold text-primary-foreground sm:min-h-9";

  return (
    <nav
      className="mt-8 flex flex-wrap items-center justify-center gap-2"
      aria-label="Product pages"
    >
      {page > 1 ? (
        <Link
          href={hrefFor(page - 1)}
          onClick={(e) => handleClick(e, page - 1)}
          rel="prev"
          className={linkClass}
        >
          Previous
        </Link>
      ) : (
        // Page 1 has no previous URL — keep the control visible but inert
        // rather than emitting an <a> that points at the page you're on.
        <Button variant="outline" disabled>
          Previous
        </Button>
      )}

      {pageWindow(page, totalPages).map((p, i) =>
        p === "gap" ? (
          <span
            key={`gap-${i}`}
            aria-hidden="true"
            className="px-1 text-sm text-muted-foreground"
          >
            …
          </span>
        ) : p === page ? (
          <span key={p} aria-current="page" className={activeClass}>
            {p}
          </span>
        ) : (
          <Link
            key={p}
            href={hrefFor(p)}
            onClick={(e) => handleClick(e, p)}
            className={linkClass}
            aria-label={`Page ${p}`}
          >
            {p}
          </Link>
        )
      )}

      {page < totalPages ? (
        <Link
          href={hrefFor(page + 1)}
          onClick={(e) => handleClick(e, page + 1)}
          rel="next"
          className={linkClass}
        >
          Next
        </Link>
      ) : (
        <Button variant="outline" disabled>
          Next
        </Button>
      )}

      <span className="sr-only">
        Page {page} of {totalPages}
      </span>
    </nav>
  );
}
